// 🎬🔗 **클링 컷 계약 — 값·상한·술어의 정본**(2026-09-10 · 회장님 확정).
//
// ══ 왜 별도 모듈인가 ═══════════════════════════════════════════════════════════════════════
// `omni-cut-contract` 와 ★같은 관용구★ 다: 벤더 하나가 「어떤 genMode 값인가 · 컷이 몇 초까지 되나 ·
//   이 컷이 그 벤더인가」를 한 자리에서 소유한다. 이 셋이 흩어지면 화면·계획·제출이 다른 상한을 믿고
//   조용히 갈린다(옴니가 그 이유로 자기 contract 를 가진다).
//
// ══ 왜 클링인가(요약 — 근거는 정본 문서가 소유한다) ════════════════════════════════════════
// `docs/01-plan/features/kling-vendor-integration.md` — 시댄스가 거절하는 실사 인물을 클링은 받고
//   (실측 2026-09-09), 구글 옴니 1.1 은 실패가 잦다는 것이 회장님 판단이다. **그 자리를 대체한다.**
// ★고치기(대사 수정) 영역은 대상이 아니다★ — `dialogue-fix-route` 의 `omni-inplace` 는 옴니의
//   이어받기 id 로 「같은 영상 위 그 문장만」 교체하는 기능이고 클링에 대응물이 없다(정본 §8-2).
import type { SceneGenMode } from "../types/scene-gen-mode";

/**
 * 🎬 **클링 O3(옴니) 컷의 `genMode` 값** — 저장분에 박히는 문자열이라 ★한 번 정하면 못 바꾼다★
 *   (옛 작업물이 이 값을 들고 있게 된다 · `OMNI11_GEN_MODE` 와 같은 규약).
 * ★`satisfies` 로 유니온에 묶는다★ — 타입이 먼저 red 를 내므로 유니온에 값을 안 더한 채 이 상수만
 *   추가하는 반쪽 상태가 원리적으로 안 생긴다.
 */
export const KLING_O3_GEN_MODE = "klingo3" as const satisfies NonNullable<SceneGenMode>;

/**
 * 🎬 **EF `generate-kling-video` 에 넘기는 `model` 별칭** — Atlas 모델 ID 는 EF 의 `modelMap` 이
 *   소유한다(`kwaivgi/kling-video-o3-std/image-to-video`). ★여기서 Atlas ID 를 적지 않는다★:
 *   벤더 ID 가 바뀌는 날 EF 한 곳만 고치면 되게 둔다.
 */
export const KLING_O3_MODEL = "kling-3.0-omni" as const;

/** ⏱ 벤더 실측 상한(Atlas Cloud 모델 페이지 2026-09-09) — v3.0 계열 i2v 는 ★3~15초 정수★. */
export const KLING_MIN_SEC = 3;
export const KLING_MAX_SEC = 15;

/**
 * ⏱ 컷 길이 클램프 — 정수 3~15.
 * ★상한이 옴니(10초)보다 길다★: 썰 레인의 컷 계약(15초)과 정확히 같아, 옴니에서 접히던 긴 컷이
 *   클링에서는 그대로 나간다. 접기는 여기 한 곳에서만 한다(호출부가 손으로 min/max 를 적지 않는다).
 */
export function klingDurationSec(sec: unknown): number {
  const n = Math.round(Number(sec));
  if (!Number.isFinite(n)) return KLING_MIN_SEC;
  return Math.min(KLING_MAX_SEC, Math.max(KLING_MIN_SEC, n));
}

/** 🎬 이 컷이 클링 O3 인가 — 판정은 이 술어 하나(리터럴 비교 금지 · `isOmni11Cut` 관용구). */
export function isKlingO3Cut(genMode: unknown): boolean {
  return genMode === KLING_O3_GEN_MODE;
}

/**
 * 🔇 **클링 컷은 ★무음으로★ 굽는다**(회장님 확정 2026-09-10).
 *
 * ★왜 상수인가★: 이 레인의 목소리는 **우리 TTS** 가 낸다(정본 §3-1). 클링이 만든 환경음·발화가
 *   함께 실리면 ①발음 레인이 원천 차단해 둔 환경음이 그 컷에서 되살아나고 ②우리 TTS 와 이중
 *   발화가 된다. 그래서 «끌 수 있으면 끈다» 가 아니라 ★언제나 끈다★ 가 계약이다.
 * ⚠️ **터보 티어는 이 스위치가 없다**(Atlas 스키마 미노출 · 2026-09-10 실측: `sound:false` 를 보내도
 *   오디오가 붙어 나왔다). O3 를 고른 이유 중 하나가 이것이다.
 */
export const KLING_SOUND_ON = false;

/**
 * 💰 **EF 가 정하는 과금 키의 프론트 사본** — 정본은 `generate-kling-video` 의 `billingModel` 갈래이고
 *   (`atlasModel.includes('kling-video-o3') → 'kling-o3-std'`), 단가 행은 2026-09-10 마이그레이션이 세운다.
 * ★왜 프론트가 이 값을 아는가★: **견적이 청구와 같은 키를 읽어야 한다**. 견적이 시댄스 단가로 나가고
 *   청구만 클링 단가면 그건 이 레포가 반복해 막아 온 「표기≠실차감」이다(`omniBillingModelOf` 와 같은 자리).
 * ★키 이름을 여기서 지어내지 않는다★ — EF 문자열과 ★바이트 동일★ 해야 3단 폴백이 와일드카드로 안 떨어진다
 *   (와일드카드는 무료가 아니라 ★남의 단가★ 다 — `model-rename-silently-rerates-via-wildcard` 교훈).
 */
export const KLING_O3_BILLING_MODEL = "kling-o3-std" as const;
